import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBuildingBank,
  IconCalendarEvent,
  IconFiles,
  IconFileText,
  IconGavel,
  IconMail,
  IconPhone,
  IconSearch,
  IconThumbDown,
  IconThumbUp,
  IconVideo,
  IconWorldWww,
} from '@tabler/icons-react'
import { tokens } from '@/theme'
import { trackFunnel } from '@/utils/analytics'
import { apiUrl } from '@/lib/apiBase'

// ─── API response types (derived from Pydantic schemas) ──────────────────────

interface GSAOut {
  gsa_id: number
  name: string
  name_slug: string
  basin_id: number
  legal_entity_type: string | null
  boundary_status: string | null
  source_url: string | null
}

/** Parallel GSASummaryItem — has county/dwr_region/website not in GSAOut */
interface GSASummaryItem {
  gsa_id: number
  name: string
  name_slug: string
  county: string | null
  dwr_region: string | null
  legal_entity_type: string | null
  website: string | null
}

interface GSAContactOut {
  contact_id: number
  gsa_id: number
  contact_name: string | null
  contact_title: string | null
  contact_email: string | null
  contact_phone: string | null
  contact_type: string | null
}

interface MeetingOut {
  meeting_id: string
  gsa_id: number | null
  meeting_type: string | null
  meeting_date: string
  location: string | null
  source_agenda_url: string | null
  source_minutes_url: string | null
  source_video_url: string | null
}

interface DecisionOut {
  decision_id: string
  meeting_id: string
  summary: string | null
  outcome: string | null
  vote_for: number | null
  vote_against: number | null
  vote_abstain: number | null
  vote_summary: string | null
}

interface NewsOut {
  id: number
  title: string
  summary: string | null
  source_url: string | null
  published_at: string | null
  category: string | null
}

interface Page<T> {
  items: T[]
  total: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function outcomeColor(outcome: string | null): 'success' | 'error' | 'default' {
  if (!outcome) return 'default'
  const lower = outcome.toLowerCase()
  if (lower.includes('approv') || lower.includes('pass') || lower.includes('adopt')) return 'success'
  if (lower.includes('den') || lower.includes('reject') || lower.includes('fail')) return 'error'
  return 'default'
}

/** Flags meeting dates that are implausibly far in the past/future — likely a
 * source-data parsing error rather than a real scheduled meeting. */
function isAnomalousDate(dateStr: string): boolean {
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return true
  const yearsFromNow = d.getFullYear() - new Date().getFullYear()
  return yearsFromNow > 3 || yearsFromNow < -50
}

function isUpcoming(dateStr: string): boolean {
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return false
  return d.getTime() >= new Date().setHours(0, 0, 0, 0)
}

// ─── Sub-card skeleton ────────────────────────────────────────────────────────

function InnerSkeleton() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
      ))}
    </>
  )
}

// ─── Hero skeleton ────────────────────────────────────────────────────────────

function HeroSkeleton() {
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${tokens.green.dark} 0%, ${tokens.green.main} 100%)`,
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Skeleton variant="text" width="25%" height={20} sx={{ mb: 1, bgcolor: alpha('#fff', 0.2) }} />
        <Skeleton variant="text" width="60%" height={56} sx={{ mb: 1, bgcolor: alpha('#fff', 0.2) }} />
        <Skeleton variant="text" width="40%" height={28} sx={{ mb: 3, bgcolor: alpha('#fff', 0.2) }} />
        <Stack direction="row" spacing={2}>
          <Skeleton variant="rounded" width={120} height={36} sx={{ bgcolor: alpha('#fff', 0.2) }} />
        </Stack>
      </Container>
    </Box>
  )
}

// ─── Contacts card ────────────────────────────────────────────────────────────

function initials(name: string | null): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('')
}

function ContactsCard({
  contacts,
  loading,
  error,
}: {
  contacts: GSAContactOut[]
  loading: boolean
  error: string | null
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
          Contacts
        </Typography>
        {loading && <InnerSkeleton />}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && contacts.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No contact information available for this GSA.
          </Typography>
        )}
        {!loading && !error && contacts.length > 0 && (
          <Stack divider={<Divider />} spacing={1.5}>
            {contacts.map((c) => (
              <Box key={c.contact_id} sx={{ pt: 0.5 }}>
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: tokens.green[100],
                      color: tokens.green.dark,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      flexShrink: 0,
                    }}
                  >
                    {initials(c.contact_name)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {c.contact_name ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {c.contact_title ?? '—'}
                      {c.contact_type ? ` · ${c.contact_type}` : ''}
                    </Typography>
                  </Box>
                </Stack>
                <Stack spacing={0.5} sx={{ pl: 0.25 }}>
                  {c.contact_email && (
                    <MuiLink
                      href={`mailto:${c.contact_email}`}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontSize: '0.8rem',
                        color: tokens.green.main,
                      }}
                    >
                      <IconMail size={14} /> {c.contact_email}
                    </MuiLink>
                  )}
                  {c.contact_phone && (
                    <MuiLink
                      href={`tel:${c.contact_phone}`}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                      }}
                    >
                      <IconPhone size={14} /> {c.contact_phone}
                    </MuiLink>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Meetings list (used inside tabs) ────────────────────────────────────────

const MEETINGS_PAGE_SIZE = 20

function MeetingsList({
  meetings,
  loading,
  error,
}: {
  meetings: MeetingOut[]
  loading: boolean
  error: string | null
}) {
  const [meetingsTab, setMeetingsTab] = useState<'upcoming' | 'past'>('upcoming')
  const [query, setQuery] = useState('')
  const [docFilter, setDocFilter] = useState<'all' | 'agenda' | 'minutes'>('all')
  const [visibleCount, setVisibleCount] = useState(MEETINGS_PAGE_SIZE)

  // Anomalous dates (e.g. a typo'd year far in the future) are routed into
  // Past rather than Upcoming — they're data errors, not real future meetings.
  const upcomingMeetings = useMemo(
    () => meetings.filter((m) => isUpcoming(m.meeting_date) && !isAnomalousDate(m.meeting_date)),
    [meetings],
  )
  const pastMeetings = useMemo(
    () => meetings.filter((m) => !isUpcoming(m.meeting_date) || isAnomalousDate(m.meeting_date)),
    [meetings],
  )

  // If there are no upcoming meetings, default the toolbar to Past so the
  // list isn't empty on first load.
  useEffect(() => {
    if (upcomingMeetings.length === 0 && pastMeetings.length > 0) setMeetingsTab('past')
  }, [upcomingMeetings.length, pastMeetings.length])

  const filtered = useMemo(() => {
    const base = meetingsTab === 'upcoming' ? upcomingMeetings : pastMeetings
    const q = query.trim().toLowerCase()
    return base.filter((m) => {
      if (docFilter === 'agenda' && !m.source_agenda_url) return false
      if (docFilter === 'minutes' && !m.source_minutes_url) return false
      if (!q) return true
      return (
        formatDate(m.meeting_date).toLowerCase().includes(q) ||
        (m.meeting_type ?? '').toLowerCase().includes(q) ||
        (m.location ?? '').toLowerCase().includes(q)
      )
    })
  }, [meetingsTab, upcomingMeetings, pastMeetings, query, docFilter])

  useEffect(() => setVisibleCount(MEETINGS_PAGE_SIZE), [meetingsTab, query, docFilter])

  if (loading) return <InnerSkeleton />
  if (error) return <Alert severity="error">{error}</Alert>
  if (meetings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No meeting records found for this GSA.
      </Typography>
    )
  }

  const visible = filtered.slice(0, visibleCount)

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={meetingsTab}
          onChange={(_, v) => v && setMeetingsTab(v)}
        >
          <ToggleButton value="upcoming" sx={{ textTransform: 'none', px: 1.75 }}>
            Upcoming ({upcomingMeetings.length})
          </ToggleButton>
          <ToggleButton value="past" sx={{ textTransform: 'none', px: 1.75 }}>
            Past ({pastMeetings.length})
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search meetings…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ width: 180 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch size={15} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Select
            size="small"
            value={docFilter}
            onChange={(e) => setDocFilter(e.target.value as typeof docFilter)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">All docs</MenuItem>
            <MenuItem value="agenda">Has agenda</MenuItem>
            <MenuItem value="minutes">Has minutes</MenuItem>
          </Select>
        </Stack>
      </Stack>

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No meetings match your filters.
        </Typography>
      ) : (
        <>
          <Stack divider={<Divider />}>
            {visible.map((m) => {
              const anomalous = isAnomalousDate(m.meeting_date)
              return (
                <Box key={m.meeting_id} sx={{ py: 1.5 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
                  >
                    <Box>
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatDate(m.meeting_date)}
                        </Typography>
                        {anomalous && (
                          <Tooltip title="This date looks implausible — likely a source-data parsing error.">
                            <Chip
                              icon={<IconAlertTriangle size={12} />}
                              label="check date"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      {m.meeting_type && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: '"DM Mono", monospace',
                            color: 'text.secondary',
                            textTransform: 'capitalize',
                          }}
                        >
                          {m.meeting_type}
                        </Typography>
                      )}
                      {m.location && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: 'text.secondary' }}
                        >
                          {m.location}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
                      {m.source_agenda_url && (
                        <Tooltip title="Agenda document">
                          <MuiLink
                            href={m.source_agenda_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              fontSize: '0.75rem',
                              fontFamily: '"DM Mono", monospace',
                              color: tokens.green.dark,
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            <IconFileText size={13} /> Agenda
                          </MuiLink>
                        </Tooltip>
                      )}
                      {m.source_minutes_url && (
                        <Tooltip title="Meeting minutes">
                          <MuiLink
                            href={m.source_minutes_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              fontSize: '0.75rem',
                              fontFamily: '"DM Mono", monospace',
                              color: tokens.blue.main,
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            <IconFileText size={13} /> Minutes
                          </MuiLink>
                        </Tooltip>
                      )}
                      {m.source_video_url && (
                        <Tooltip title="Meeting video">
                          <MuiLink
                            href={m.source_video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              fontSize: '0.75rem',
                              fontFamily: '"DM Mono", monospace',
                              color: '#C4501A',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            <IconVideo size={13} /> Video
                          </MuiLink>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
          {filtered.length > visible.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setVisibleCount((c) => c + MEETINGS_PAGE_SIZE)}
              >
                Show {Math.min(MEETINGS_PAGE_SIZE, filtered.length - visible.length)} more
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  )
}

// ─── Last Agenda Panel ────────────────────────────────────────────────────────

function LastAgendaPanel({ gsaId }: { gsaId: string }) {
  const [meeting, setMeeting] = useState<MeetingOut | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!gsaId) return
    setLoading(true)
    setError(null)
    fetch(
      apiUrl(`/api/agro/gsas/${encodeURIComponent(gsaId)}/meetings?page_size=20&sort=meeting_date&order=desc`),
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Page<MeetingOut>) => {
        const found = data.items.find((m) => !!m.source_agenda_url) ?? null
        setMeeting(found)
      })
      .catch(() => setError('Failed to load agenda data.'))
      .finally(() => setLoading(false))
  }, [gsaId])

  if (loading) {
    return (
      <>
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={700} sx={{ borderRadius: 1 }} />
      </>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (!meeting || !meeting.source_agenda_url) {
    return (
      <Typography variant="body2" color="text.secondary">
        No agenda documents found for this GSA.
      </Typography>
    )
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatDate(meeting.meeting_date)}
          </Typography>
          {meeting.meeting_type && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"DM Mono", monospace',
                color: 'text.secondary',
                textTransform: 'capitalize',
              }}
            >
              {meeting.meeting_type}
            </Typography>
          )}
        </Box>
        <Button
          component="a"
          href={meeting.source_agenda_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="small"
          startIcon={<IconFileText size={15} />}
          sx={{
            background: tokens.green.dark,
            color: '#fff',
            textTransform: 'none',
            fontFamily: '"Syne", sans-serif',
            fontWeight: 600,
            '&:hover': { background: tokens.green.main },
          }}
        >
          Open Agenda PDF
        </Button>
      </Stack>
      <Box
        component="iframe"
        src={meeting.source_agenda_url}
        width="100%"
        height="700px"
        sx={{ border: 'none', borderRadius: 1, display: 'block' }}
        title="Agenda PDF"
      />
    </Box>
  )
}

// ─── Decisions card ───────────────────────────────────────────────────────────

function DecisionsCard({
  decisions,
  loading,
  error,
}: {
  decisions: DecisionOut[]
  loading: boolean
  error: string | null
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: tokens.green.dark }}>
          Recent Decisions
        </Typography>
        {loading && <InnerSkeleton />}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && decisions.length === 0 && (
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
            <IconGavel size={18} style={{ color: tokens.textMuted, marginTop: 1, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              No decision records extracted yet for this GSA. Check the Meetings tab for the latest
              agendas and minutes in the meantime.
            </Typography>
          </Stack>
        )}
        {!loading && !error && decisions.length > 0 && (
          <Stack divider={<Divider />}>
            {decisions.map((d) => (
              <Box key={d.decision_id} sx={{ py: 1.5 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ alignItems: { sm: 'flex-start' } }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {d.summary ?? 'No summary available.'}
                    </Typography>
                    {(d.vote_for != null || d.vote_against != null || d.vote_abstain != null) && (
                      <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }}>
                        {d.vote_for != null && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              fontFamily: '"DM Mono", monospace',
                              color: '#4C8F3A',
                            }}
                          >
                            <IconThumbUp size={12} /> {d.vote_for} for
                          </Typography>
                        )}
                        {d.vote_against != null && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              fontFamily: '"DM Mono", monospace',
                              color: '#C62828',
                            }}
                          >
                            <IconThumbDown size={12} /> {d.vote_against} against
                          </Typography>
                        )}
                        {d.vote_abstain != null && d.vote_abstain > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: '"DM Mono", monospace', color: 'text.secondary' }}
                          >
                            {d.vote_abstain} abstain
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {d.vote_summary && d.vote_for == null && d.vote_against == null && (
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: '"DM Mono", monospace', color: 'text.secondary' }}
                      >
                        {d.vote_summary}
                      </Typography>
                    )}
                  </Box>
                  {d.outcome && (
                    <Chip
                      label={d.outcome}
                      size="small"
                      color={outcomeColor(d.outcome)}
                      sx={{ flexShrink: 0, fontSize: '0.72rem', textTransform: 'capitalize' }}
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

// ─── News card ────────────────────────────────────────────────────────────────

function NewsCard({
  news,
  loading,
  error,
}: {
  news: NewsOut[]
  loading: boolean
  error: string | null
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: tokens.green.dark }}>
          Recent News
        </Typography>
        {loading && <InnerSkeleton />}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && news.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No news items found for this GSA.
          </Typography>
        )}
        {!loading && !error && news.length > 0 && (
          <Stack divider={<Divider />}>
            {news.map((n) => (
              <Box key={n.id} sx={{ py: 1.5 }}>
                {n.source_url ? (
                  <MuiLink
                    href={n.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: tokens.green.main,
                      textDecoration: 'none',
                      mb: 0.5,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {n.title}
                  </MuiLink>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {n.title}
                  </Typography>
                )}
                {n.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {n.summary}
                  </Typography>
                )}
                {n.published_at && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: '"DM Mono", monospace',
                      color: 'text.secondary',
                      display: 'block',
                      mt: 0.5,
                    }}
                  >
                    {formatDate(n.published_at)}
                    {n.category ? ` · ${n.category}` : ''}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function GSADetailPage() {
  const { nameSlug } = useParams<{ nameSlug: string }>()
  const navigate = useNavigate()

  // Resolved real gsa_id (set after we receive GSAOut from the slug-aware endpoint)
  const [resolvedGsaId, setResolvedGsaId] = useState<string>('')

  // GSA profile (GSAOut from /gsas/{nameSlug} — API resolves slug)
  const [gsa, setGsa] = useState<GSAOut | null>(null)
  // Summary row (GSASummaryItem from /gsas/summary — has county/website/dwr_region)
  const [gsaSummary, setGsaSummary] = useState<GSASummaryItem | null>(null)
  const [gsaLoading, setGsaLoading] = useState(true)
  const [gsaError, setGsaError] = useState<string | null>(null)

  const [contacts, setContacts] = useState<GSAContactOut[]>([])
  const [contactsLoading, setContactsLoading] = useState(true)
  const [contactsError, setContactsError] = useState<string | null>(null)

  const [meetings, setMeetings] = useState<MeetingOut[]>([])
  const [meetingsLoading, setMeetingsLoading] = useState(true)
  const [meetingsError, setMeetingsError] = useState<string | null>(null)
  const [meetingsTotal, setMeetingsTotal] = useState(0)

  const [decisions, setDecisions] = useState<DecisionOut[]>([])
  const [decisionsLoading, setDecisionsLoading] = useState(true)
  const [decisionsError, setDecisionsError] = useState<string | null>(null)
  const [decisionsTotal, setDecisionsTotal] = useState(0)

  const [newsItems, setNewsItems] = useState<NewsOut[]>([])
  const [newsLoading, setNewsLoading] = useState(true)
  const [newsError, setNewsError] = useState<string | null>(null)

  // Tab state for the meetings/agenda tabbed section
  const [tab, setTab] = useState<'meetings' | 'agenda'>('meetings')

  // Privacy-safe funnel: record one coarse "GSA profile viewed" event per slug.
  // We send only the surface and step — never the GSA id, slug, or name — so
  // the event cannot identify a visitor or single-out a record. (T-147)
  useEffect(() => {
    if (!nameSlug) return
    trackFunnel('gsa', { surface: 'gsa_detail', step: 'view' })
  }, [nameSlug])

  // GSA profile + summary (resolves slug via API)
  useEffect(() => {
    if (!nameSlug) return
    setGsaLoading(true)
    setGsaError(null)
    fetch(apiUrl(`/api/agro/gsas/${encodeURIComponent(nameSlug)}`))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: GSAOut) => {
        setGsa(data)
        setResolvedGsaId(String(data.gsa_id))
      })
      .catch(() => setGsaError('Failed to load GSA profile.'))
      .finally(() => setGsaLoading(false))

    // Piggyback the summary endpoint for county/region/website fields
    fetch(apiUrl('/api/agro/gsas/summary'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((list: GSASummaryItem[]) => {
        // Match by slug (name_slug) since we only have the slug at this point
        const match = list.find((g) => g.name_slug === nameSlug) ?? null
        if (match) setGsaSummary(match)
      })
      .catch(() => {
        // non-fatal — hero still works from GSAOut
      })
  }, [nameSlug])

  // Contacts — depends on resolvedGsaId
  useEffect(() => {
    if (!resolvedGsaId) return
    setContactsLoading(true)
    setContactsError(null)
    fetch(apiUrl(`/api/agro/gsas/${encodeURIComponent(resolvedGsaId)}/contacts?page_size=50`))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Page<GSAContactOut>) => setContacts(data.items))
      .catch(() => setContactsError('Failed to load contacts.'))
      .finally(() => setContactsLoading(false))
  }, [resolvedGsaId])

  // Meetings — depends on resolvedGsaId. Fetched in full (not server-paginated)
  // so the toolbar below can search/filter/split upcoming-vs-past on the client.
  useEffect(() => {
    if (!resolvedGsaId) return
    setMeetingsLoading(true)
    setMeetingsError(null)
    fetch(
      apiUrl(`/api/agro/gsas/${encodeURIComponent(resolvedGsaId)}/meetings?page_size=500&sort=meeting_date&order=desc`),
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Page<MeetingOut>) => {
        setMeetings(data.items)
        setMeetingsTotal(data.total)
      })
      .catch(() => setMeetingsError('Failed to load meetings.'))
      .finally(() => setMeetingsLoading(false))
  }, [resolvedGsaId])

  // Decisions — depends on resolvedGsaId
  useEffect(() => {
    if (!resolvedGsaId) return
    setDecisionsLoading(true)
    setDecisionsError(null)
    fetch(
      apiUrl(`/api/agro/gsas/${encodeURIComponent(resolvedGsaId)}/decisions?page_size=10&sort=created_at&order=desc`),
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Page<DecisionOut>) => {
        setDecisions(data.items)
        setDecisionsTotal(data.total)
      })
      .catch(() => setDecisionsError('Failed to load decisions.'))
      .finally(() => setDecisionsLoading(false))
  }, [resolvedGsaId])

  // News — depends on resolvedGsaId
  useEffect(() => {
    if (!resolvedGsaId) return
    setNewsLoading(true)
    setNewsError(null)
    fetch(
      apiUrl(`/api/agro/gsas/${encodeURIComponent(resolvedGsaId)}/news?page_size=5&sort=published_at&order=desc`),
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Page<NewsOut>) => setNewsItems(data.items))
      .catch(() => setNewsError('Failed to load news.'))
      .finally(() => setNewsLoading(false))
  }, [resolvedGsaId])

  const displayName = gsa?.name ?? gsaSummary?.name ?? nameSlug ?? 'GSA Profile'
  const county = gsaSummary?.county ?? null
  const dwr_region = gsaSummary?.dwr_region ?? null
  const website = gsaSummary?.website ?? null
  const boundary_status = gsa?.boundary_status ?? null
  const legal_entity_type = gsa?.legal_entity_type ?? gsaSummary?.legal_entity_type ?? null

  const nextMeeting = useMemo(() => {
    const upcoming = meetings.filter((m) => isUpcoming(m.meeting_date) && !isAnomalousDate(m.meeting_date))
    if (upcoming.length === 0) return null
    return upcoming.reduce((earliest, m) =>
      new Date(m.meeting_date) < new Date(earliest.meeting_date) ? m : earliest,
    )
  }, [meetings])

  const documentCount = useMemo(
    () =>
      meetings.reduce(
        (n, m) => n + (m.source_agenda_url ? 1 : 0) + (m.source_minutes_url ? 1 : 0) + (m.source_video_url ? 1 : 0),
        0,
      ),
    [meetings],
  )

  return (
    <Box sx={{ background: tokens.cream, minHeight: '100vh' }}>
      {/* Hero */}
      {gsaLoading && !gsa ? (
        <HeroSkeleton />
      ) : gsaError ? (
        <Box sx={{ background: tokens.green.dark, py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Alert severity="error" sx={{ mb: 2 }}>
              {gsaError}
            </Alert>
            <Button
              variant="outlined"
              startIcon={<IconArrowLeft size={16} />}
              onClick={() => navigate('/gsa-data')}
              sx={{ color: '#fff', borderColor: alpha('#fff', 0.4) }}
            >
              Back to GSA Explorer
            </Button>
          </Container>
        </Box>
      ) : (
        <Box
          sx={{
            background: `linear-gradient(135deg, ${tokens.green.dark} 0%, ${tokens.green.main} 100%)`,
            color: '#fff',
            py: { xs: 4, md: 5 },
          }}
        >
          <Container maxWidth="lg">
            {/* Back breadcrumb */}
            <Button
              size="small"
              startIcon={<IconArrowLeft size={15} />}
              onClick={() => navigate('/gsa-data')}
              sx={{
                mb: 1.5,
                color: alpha('#fff', 0.75),
                fontSize: '0.8rem',
                fontFamily: '"DM Mono", monospace',
                textTransform: 'none',
                px: 0,
                minWidth: 0,
                '&:hover': { color: '#fff', background: 'transparent' },
              }}
            >
              Back to GSA Explorer
            </Button>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-end' } }}
            >
              <Box>
                {/* Overline */}
                <Typography
                  variant="overline"
                  sx={{ color: alpha('#fff', 0.6), display: 'block', mb: 0.5, letterSpacing: '0.15em' }}
                >
                  Groundwater Sustainability Agency
                </Typography>

                {/* Name */}
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontSize: { xs: '1.6rem', md: '2.1rem' },
                    fontWeight: 700,
                    mb: 1,
                    lineHeight: 1.15,
                  }}
                >
                  {displayName}
                </Typography>

                {/* Location */}
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {county && (
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8) }}>
                      {county} County
                    </Typography>
                  )}
                  {county && dwr_region && (
                    <Typography sx={{ color: alpha('#fff', 0.4) }}>·</Typography>
                  )}
                  {dwr_region && (
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8) }}>
                      DWR Region: {dwr_region}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Chips + website */}
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                {boundary_status && (
                  <Chip
                    icon={<IconBuildingBank size={13} />}
                    label={boundary_status}
                    size="small"
                    sx={{
                      background: alpha('#fff', 0.15),
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontFamily: '"DM Mono", monospace',
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                  />
                )}
                {legal_entity_type && (
                  <Chip
                    label={legal_entity_type}
                    size="small"
                    sx={{
                      background: alpha('#fff', 0.12),
                      color: alpha('#fff', 0.85),
                      fontSize: '0.72rem',
                      fontFamily: '"DM Mono", monospace',
                    }}
                  />
                )}
                {website && (
                  <Button
                    component="a"
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    startIcon={<IconWorldWww size={15} />}
                    variant="outlined"
                    sx={{
                      color: '#fff',
                      borderColor: alpha('#fff', 0.4),
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': { borderColor: '#fff', background: alpha('#fff', 0.08) },
                    }}
                  >
                    Official Website
                  </Button>
                )}
              </Stack>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Stat strip */}
      {!gsaLoading && !gsaError && (
        <Box sx={{ background: '#fff', borderBottom: `1px solid ${alpha(tokens.green.main, 0.1)}` }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 0,
              }}
            >
              {[
                {
                  icon: <IconCalendarEvent size={16} />,
                  label: 'Total meetings',
                  value: meetingsLoading ? '—' : meetingsTotal.toLocaleString(),
                },
                {
                  icon: <IconCalendarEvent size={16} />,
                  label: 'Next meeting',
                  value: meetingsLoading ? '—' : nextMeeting ? formatDate(nextMeeting.meeting_date) : 'None scheduled',
                },
                {
                  icon: <IconFiles size={16} />,
                  label: 'Documents',
                  value: meetingsLoading ? '—' : documentCount.toLocaleString(),
                },
                {
                  icon: <IconGavel size={16} />,
                  label: 'Decisions logged',
                  value: decisionsLoading ? '—' : decisionsTotal.toLocaleString(),
                },
              ].map((s) => (
                <Box
                  key={s.label}
                  sx={{ py: 1.5, px: { xs: 0, sm: 2 }, borderLeft: { sm: `1px solid ${alpha(tokens.green.main, 0.08)}` }, '&:first-of-type': { borderLeft: 'none' } }}
                >
                  <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', color: 'text.secondary', mb: 0.25 }}>
                    {s.icon}
                    <Typography variant="caption" sx={{ textTransform: 'none', letterSpacing: 0 }}>
                      {s.label}
                    </Typography>
                  </Stack>
                  <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 300px' },
            gap: 3,
          }}
        >
          {/* Main column */}
          <Box sx={{ minWidth: 0 }}>
            {/* Meetings + Last Agenda tabbed card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="Meetings" value="meetings" />
                  <Tab label="Last Agenda" value="agenda" />
                </Tabs>

                {tab === 'meetings' && (
                  <MeetingsList meetings={meetings} loading={meetingsLoading} error={meetingsError} />
                )}
                {tab === 'agenda' && resolvedGsaId && (
                  <LastAgendaPanel gsaId={resolvedGsaId} />
                )}
                {tab === 'agenda' && !resolvedGsaId && (
                  <InnerSkeleton />
                )}
              </CardContent>
            </Card>

            <DecisionsCard decisions={decisions} loading={decisionsLoading} error={decisionsError} />
          </Box>

          {/* Sidebar */}
          <Box sx={{ minWidth: 0 }}>
            <ContactsCard contacts={contacts} loading={contactsLoading} error={contactsError} />
            <NewsCard news={newsItems} loading={newsLoading} error={newsError} />
          </Box>
        </Box>

        {/* Bottom back */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<IconArrowLeft size={16} />}
            onClick={() => navigate('/gsa-data')}
          >
            Back to GSA Explorer
          </Button>
        </Box>
      </Container>
    </Box>
  )
}
